import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Inbox, Star, Calendar, Package, Wrench, X, CheckCheck } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  new_intake:      { icon: Inbox,    color: "text-blue-400",   bg: "bg-blue-500/10",   border: "border-blue-500/20"   },
  new_review:      { icon: Star,     color: "text-amber-400",  bg: "bg-amber-500/10",  border: "border-amber-500/20"  },
  new_appointment: { icon: Calendar, color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20" },
  ready_not_picked:{ icon: Wrench,   color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  low_stock:       { icon: Package,  color: "text-red-400",    bg: "bg-red-500/10",    border: "border-red-500/20"    },
};

const SEVERITY_DOT = {
  danger:  "bg-red-500",
  warning: "bg-amber-500",
  success: "bg-emerald-500",
  info:    "bg-blue-500",
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "À l'instant";
  if (diff < 3600)  return `${Math.floor(diff/60)} min`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h`;
  return `${Math.floor(diff/86400)}j`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const panelRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {}
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleMarkRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/notifications/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_new: false })));
    } catch (err) {}
  };

  const handleNotifClick = (notif) => {
    setOpen(false);
    navigate(notif.link);
  };

  const newNotifs    = notifications.filter(n => n.is_new);
  const otherNotifs  = notifications.filter(n => !n.is_new);

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-secondary transition-colors"
        title="Notifications"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-h-[80vh] bg-card border border-border rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary" />
              <span className="font-semibold text-foreground text-sm">Notifications</span>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                  {unreadCount} nouveau{unreadCount > 1 ? "x" : ""}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button onClick={handleMarkRead}
                  className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 px-2 py-1 rounded hover:bg-secondary transition-colors"
                  title="Tout marquer comme lu">
                  <CheckCheck className="w-3.5 h-3.5" />
                  Tout lire
                </button>
              )}
              <button onClick={() => setOpen(false)}
                className="p-1 hover:bg-secondary rounded transition-colors">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              <>
                {/* Nouvelles notifications */}
                {newNotifs.length > 0 && (
                  <div>
                    <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-secondary/30">
                      Nouvelles
                    </p>
                    {newNotifs.map(notif => (
                      <NotifItem key={notif.id} notif={notif} onClick={() => handleNotifClick(notif)} />
                    ))}
                  </div>
                )}
                {/* Autres notifications */}
                {otherNotifs.length > 0 && (
                  <div>
                    {newNotifs.length > 0 && (
                      <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-secondary/30">
                        Autres alertes
                      </p>
                    )}
                    {otherNotifs.map(notif => (
                      <NotifItem key={notif.id} notif={notif} onClick={() => handleNotifClick(notif)} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-border shrink-0 text-center">
              <p className="text-xs text-muted-foreground">
                {notifications.length} notification{notifications.length > 1 ? "s" : ""} au total
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotifItem({ notif, onClick }) {
  const cfg = TYPE_CONFIG[notif.type] || TYPE_CONFIG.new_intake;
  const Icon = cfg.icon;

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors text-left border-b border-border/50 last:border-0 ${notif.is_new ? "bg-primary/5" : ""}`}
    >
      {/* Icône */}
      <div className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
        <Icon className={`w-4 h-4 ${cfg.color}`} />
      </div>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-foreground leading-tight">{notif.title}</p>
          <div className="flex items-center gap-1 shrink-0">
            {notif.is_new && (
              <span className={`w-2 h-2 rounded-full ${SEVERITY_DOT[notif.severity] || "bg-blue-500"}`} />
            )}
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {timeAgo(notif.created_at)}
            </span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 truncate">{notif.message}</p>
      </div>
    </button>
  );
}
